import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { Skeleton } from "@/components/ui/skeleton";
import { Eye, Share2, Maximize2, TrendingUp, TrendingDown, Images } from "lucide-react";

const fmtPct = (v) => `${Math.round((v || 0) * 100)}%`;

export default function StoryImpactTab() {
  const t = useT();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/coach/story-highlights/analytics"); setData(d); }
    catch { setData({ totals: { moments: 0 }, trend: {}, moments: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (data === null) {
    return <div className="space-y-3" data-testid="soc-impact-tab">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>;
  }

  const totals = data.totals || {};
  const trend = data.trend || {};
  const moments = data.moments || [];

  if (!totals.moments) {
    return (
      <div data-testid="soc-impact-tab">
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Images className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("soc.impact_empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("soc.impact_empty_sub")}</p>
        </div>
      </div>
    );
  }

  const up = (trend.delta_pct || 0) >= 0;

  return (
    <div className="space-y-5" data-testid="soc-impact-tab">
      <p className="text-sm text-muted-foreground">{t("soc.impact_sub")}</p>

      <div className="rounded-2xl border border-border bg-card p-5">
        <p className="mb-3 font-display text-lg text-foreground">{t("soc.impact_overall")}</p>
        <div className="grid grid-cols-3 gap-3">
          <ImpactTile icon={Eye} label={t("soc.impact_views")} value={totals.views ?? 0} tone="text-primary" testId="soc-impact-views" />
          <ImpactTile icon={Share2} label={t("soc.impact_shares")} value={totals.shares ?? 0} tone="text-emerald-600" testId="soc-impact-shares" />
          <ImpactTile icon={Maximize2} label={t("soc.impact_previews")} value={totals.preview_opens ?? 0} tone="text-amber-600" testId="soc-impact-previews" />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="rounded-xl border-l-4 border-primary bg-primary/5 p-3">
            <p className="text-xs text-muted-foreground">{t("soc.impact_engagement")}</p>
            <p className="font-display text-2xl font-semibold text-foreground">{fmtPct(totals.engagement_rate)}</p>
            <p className="text-[10px] text-muted-foreground">{t("soc.impact_engagement_hint")}</p>
          </div>
          <div className="rounded-xl border-l-4 border-primary bg-primary/5 p-3">
            <p className="text-xs text-muted-foreground">{t("soc.impact_share_rate")}</p>
            <p className="font-display text-2xl font-semibold text-foreground">{fmtPct(totals.share_rate)}</p>
            <p className="text-[10px] text-muted-foreground">{t("soc.impact_share_hint")}</p>
          </div>
        </div>
      </div>

      <div className={`rounded-2xl border p-5 ${up ? "border-emerald-500 bg-emerald-50" : "border-rose-500 bg-rose-50"}`} data-testid="soc-impact-trend">
        <div className="flex items-center gap-2">
          {up ? <TrendingUp className="h-5 w-5 text-emerald-600" /> : <TrendingDown className="h-5 w-5 text-rose-600" />}
          <p className="text-sm font-medium text-foreground">{t("soc.impact_trend")}</p>
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <p className="font-display text-3xl font-semibold text-foreground">{trend.views_7d ?? 0}</p>
          <p className="text-xs text-muted-foreground">{t("soc.impact_views_7d")}</p>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("soc.impact_vs_prev")} {trend.views_prev_7d ?? 0} · <span className={`font-semibold ${up ? "text-emerald-600" : "text-rose-600"}`}>{up ? "+" : ""}{(trend.delta_pct || 0).toFixed(1)}%</span>
        </p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4">
        <p className="mb-2 px-1 font-display text-lg text-foreground">{t("soc.impact_ranking")}</p>
        <div className="divide-y divide-border">
          {moments.map((m, idx) => (
            <div key={m.id} data-testid={`soc-impact-moment-${m.id}`} className="flex items-start gap-3 py-3">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">#{idx + 1}</div>
              {m.cover_image_url ? (
                <img src={m.cover_image_url} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
              ) : (
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-secondary"><Images className="h-4 w-4 text-muted-foreground" /></div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-foreground">{m.title}</p>
                {m.client_first_name && <p className="text-xs text-muted-foreground">{m.client_first_name}</p>}
                <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {m.views}</span>
                  <span className="flex items-center gap-1"><Share2 className="h-3 w-3" /> {m.shares}</span>
                  <span className="flex items-center gap-1"><Maximize2 className="h-3 w-3" /> {m.preview_opens}</span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, (m.engagement_rate || 0) * 100)}%` }} />
                </div>
                <p className="mt-1 text-[10px] text-muted-foreground">{t("soc.impact_engagement")} {fmtPct(m.engagement_rate)} · {t("soc.impact_share_rate")} {fmtPct(m.share_rate)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ImpactTile({ icon: Icon, label, value, tone, testId }) {
  return (
    <div className="flex flex-col items-center rounded-xl bg-secondary p-3 text-center" data-testid={testId}>
      <Icon className={`h-4 w-4 ${tone}`} />
      <p className={`mt-1 font-display text-2xl font-semibold ${tone}`}>{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}
